import type { Character } from '@dcc/rules/domain/character.ts';
import type { PetCompanion } from '@dcc/rules/domain/pet.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { copiesOnPets } from '@dcc/rules/app/petEquipmentService.ts';
import { hotlistAllocation } from '@dcc/rules/rules/hotlist.ts';
import { GEAR_SLOT_LABELS } from '@dcc/rules/rules/inventory.ts';

export function PetEquipmentPanel({ character, pet, busy, inCombat, onEquip }: {
  character: Character; pet: PetCompanion; busy: boolean; inCombat: boolean;
  onEquip: (itemId: string, equipped: number) => Promise<boolean>;
}) {
  const rows = character.inventory.flatMap((entry) => {
    const item = contentRegistry.getItem(entry.itemId);
    if (!item?.petGear || !item.slot) return [];
    return [{ item, entry, free: entry.quantity - entry.equipped - entry.held - hotlistAllocation(character.hotlist, entry.itemId) - copiesOnPets(character, entry.itemId) }];
  });
  return <section className="dcc-creator-card">
    <h2>{pet.name} · Equipment</h2>
    <p className="dcc-creator-help">Pet equipment stays in {character.name}’s Inventory. Only specially made pet gear designated by the GM appears here. Worn gear grants its passive bonuses; pet-targeted activation of Item Powers is adjudicated by the GM.</p>
    {inCombat && <p>Change pet equipment outside combat. Ask the GM to adjudicate an equipment change during combat.</p>}
    {rows.length === 0 && <p>No pet equipment is owned yet. The GM can designate and award it through Party Inventory.</p>}
    {rows.length > 0 && <table><thead><tr><th>Item</th><th>Gear Slot</th><th>Available</th><th>Equipment</th></tr></thead><tbody>
      {rows.map(({ item, entry, free }) => {
        const worn = pet.gearItemIds.filter((id) => id === item.id).length;
        return <tr key={item.id}><td>{item.name}</td><td>{GEAR_SLOT_LABELS[item.slot!]}</td><td>{free} free / {entry.quantity} owned</td><td>
          {worn > 0 && <span>{worn} equipped </span>}
          <button disabled={busy || inCombat || free < 1} onClick={() => void onEquip(item.id, worn + 1)}>Equip 1</button>
          {worn > 0 && <button disabled={busy || inCombat} onClick={() => void onEquip(item.id, worn - 1)}>Unequip 1</button>}
        </td></tr>;
      })}
    </tbody></table>}
    {[...new Set(pet.gearItemIds)].filter((id) => !rows.some((row) => row.item.id === id)).map((id) => <p key={id}>
      {contentRegistry.getItem(id)?.name ?? id}: unavailable or not designated as pet equipment; grants no bonus. <button disabled={busy || inCombat} onClick={() => void onEquip(id, 0)}>Remove reference</button>
    </p>)}
  </section>;
}
